/**
 * SMSWiser reversible 2-level (tree) stats queries.
 * Source ▸ Carrier / Source ▸ List (Sources tab), List ▸ Source (Lists tab),
 * Gateway ▸ Carrier / Carrier ▸ Gateway (Gateways tab).
 * The report is fetched in the selected `cross` (primary_secondary) order so the
 * top-group totals come from the provider's own aggregate and match the panel.
 * For Media Devoted | Created By Ciro Autiero
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import type { CrossStatsResponse } from '@/features/smswiser-campaigns/types/workspace.types';
import api from '@/services/api';

import { keys } from '../keys';
import { useWithStatsTracker } from '../stats-tracker';
import { unwrapApiData } from '../utils';

import { LISTS_STATS_TIMEOUT_MS } from './queries-detail-stats';

export interface CrossStatsInput {
  countryKey: string;
  wiserIds: number[];
  from?: string;
  to?: string;
  tz?: string;
  currency?: string;
  /** The `primary_secondary` pair key, or null to disable. */
  cross: string | null;
  enabled?: boolean;
}

function buildCrossParams(input: CrossStatsInput): Record<string, string> {
  const wiserIds = input.wiserIds.length > 0 ? input.wiserIds : [0];
  const params: Record<string, string> = {
    wiserIds: wiserIds.join(','),
    cross: input.cross ?? '',
  };
  if (input.from) params.from = input.from;
  if (input.to) params.to = input.to;
  if (input.tz) params.tz = input.tz;
  if (input.currency) params.currency = input.currency;
  return params;
}

type StatsKeyOf = (countryKey: string, wiserIds: number[], params: Record<string, unknown>) => readonly unknown[];

function useCrossStats(
  input: CrossStatsInput,
  endpoint: 'sources-with-stats' | 'lists-with-stats' | 'gateways-with-stats',
  keyOf: StatsKeyOf,
  timeout?: number,
) {
  const wiserIds = input.wiserIds.length > 0 ? input.wiserIds : [0];
  const params = useWithStatsTracker(buildCrossParams(input));

  return useQuery({
    queryKey: keyOf(input.countryKey, wiserIds, params),
    queryFn: async () => {
      const res = await api.get(
        `/smswiser/campaigns/${input.countryKey}/${endpoint}`,
        { params, ...(timeout != null ? { timeout } : {}) },
      );
      return unwrapApiData<CrossStatsResponse>(res.data);
    },
    enabled:
      (input.enabled ?? true) &&
      input.cross != null &&
      wiserIds.length > 0 &&
      wiserIds[0] > 0 &&
      Boolean(input.from) &&
      Boolean(input.to),
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

export function useSourcesCrossStats(input: CrossStatsInput) {
  return useCrossStats(input, 'sources-with-stats', keys.sourcesWithStats);
}

export function useListsCrossStats(input: CrossStatsInput) {
  return useCrossStats(
    input,
    'lists-with-stats',
    keys.listsWithStats,
    LISTS_STATS_TIMEOUT_MS,
  );
}

export function useGatewaysCrossStats(input: CrossStatsInput) {
  return useCrossStats(input, 'gateways-with-stats', keys.gatewaysWithStats);
}
